/* eslint-disable import-extensions/file-extension-required */
import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { type EditionAwarenessUpsellProps } from '@atlassian/edition-awareness/ui/edition-awareness-upsell';
import { type Edition } from '@atlassian/edition-awareness';
import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	deserializeState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';

import type {
	useConfluenceStandardToPremiumExperimentQuery$data,
	useConfluenceStandardToPremiumExperimentQuery$variables,
} from './__generated__/useConfluenceStandardToPremiumExperimentQuery.graphql.ts';

export const EDITION_AWARENESS_STANDARD_TO_PREMIUM_EXPERIMENT_PILL_DISMISSED =
	'growth-luna-edition-awareness-standard-to-premium:treatmentPillDismissed';

const UseConfluenceStandardToPremiumExperimentQuery = graphql`
	query useConfluenceStandardToPremiumExperimentQuery($key: [String]) {
		isSiteAdmin
		onboardingState(key: $key) {
			key
			value
		}
		license {
			trialEndDate
			firstPredunningEndTime
		}
	}
`;

//Some of these checks are redundant and also called in the EditionAwarenessUpsell component
// optimization can be discuss if we decide to productionize since things with EA platform component are still in flux
export const useConfluenceStandardToPremiumExperiment = (): {
	isEligibleForUpsellExperiment: boolean;
} & EditionAwarenessUpsellProps => {
	const { data, error, loading } = useQueryInterop<
		useConfluenceStandardToPremiumExperimentQuery$data,
		useConfluenceStandardToPremiumExperimentQuery$variables
	>(UseConfluenceStandardToPremiumExperimentQuery, {
		variables: {
			key: [EDITION_AWARENESS_STANDARD_TO_PREMIUM_EXPERIMENT_PILL_DISMISSED],
		},
		fetchPolicy: 'cache-only',
	});
	const { edition: confluenceEdition, cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState } = useSetOnboardingState();

	const handleDismiss = useCallback(() => {
		setOnboardingState({
			key: EDITION_AWARENESS_STANDARD_TO_PREMIUM_EXPERIMENT_PILL_DISMISSED,
			value: 'true',
		});
	}, [setOnboardingState]);

	if (error && !isErrorMarkedAsHandled(error)) {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'failed',
				actionSubject: 'ea-standard-to-premium-query',
				source: 'useConfluenceStandardToPremiumExperiment',
				attributes: {
					growthFeature: 'EditionAwareness',
					error: error.message,
				},
			},
		}).fire();
		markErrorAsHandled(error);
	}

	// Exposure fired earlier in experiment when they see EA
	const enrolled =
		UNSAFE_expValEqualsNoExposure(
			'edition_awareness_standard_upsell_confluence_ab',
			'cohort',
			'control',
		) ||
		UNSAFE_expValEqualsNoExposure(
			'edition_awareness_standard_upsell_confluence_ab',
			'cohort',
			'treatment',
		);

	const isSiteAdmin = data?.isSiteAdmin;
	const edition = confluenceEdition?.toLocaleLowerCase() as Edition;

	const trialDaysLeft = getTrialDaysLeftFromTrialEndDate(data?.license?.trialEndDate);

	const isInTrial =
		!data || loading || error ? undefined : trialDaysLeft ? trialDaysLeft > 0 : false;

	const firstPredunningEndTime = data?.license?.firstPredunningEndTime;
	const isInPreDunning = firstPredunningEndTime ? Date.now() < firstPredunningEndTime : false;

	const { treatmentPillDismissed } = deserializeState(
		data?.onboardingState ? { onboardingState: data?.onboardingState } : null,
	);

	const isDismissed = loading || !!error || !!treatmentPillDismissed;

	const isEligibleForUpsellExperiment =
		enrolled && edition === 'standard' && isInTrial === false && !isInPreDunning && !!isSiteAdmin;

	return {
		isEligibleForUpsellExperiment,
		cloudId,
		product: 'confluence',
		edition,
		userRoles: isSiteAdmin ? ['SITE_ADMIN'] : [],
		hasDismissedButton: isDismissed,
		onDismissButton: handleDismiss,
	};
};
